import { useCallback, useEffect, useState } from "react";
import { ticketService } from "@/services/ticket.service";
import type { TicketSummary } from "@/types/ticket.types";

const UNREAD_TICKETS_EVENT = "unread-tickets-refresh";
const TICKET_LAST_SEEN_KEY = "ticket-last-seen-at";

const resolveLastSeenStorageKey = () => {
  if (typeof window === "undefined") {
    return TICKET_LAST_SEEN_KEY;
  }
  try {
    const rawUser = window.localStorage.getItem("user");
    if (!rawUser) return TICKET_LAST_SEEN_KEY;
    const parsed = JSON.parse(rawUser) as { id?: string | null } | null;
    const userId = parsed?.id?.trim();
    if (!userId) return TICKET_LAST_SEEN_KEY;
    return `${TICKET_LAST_SEEN_KEY}:${userId}`;
  } catch {
    return TICKET_LAST_SEEN_KEY;
  }
};

const readLastSeenAt = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.localStorage.getItem(resolveLastSeenStorageKey());
  } catch {
    return null;
  }
};

const writeLastSeenAt = (isoTimestamp: string) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(resolveLastSeenStorageKey(), isoTimestamp);
  } catch {
    // Ignore storage failures.
  }
};

const toTimestamp = (value: string | null | undefined) => {
  if (!value) return Number.NaN;
  return Date.parse(value);
};

export const notifyUnreadTicketsChanged = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(UNREAD_TICKETS_EVENT));
  }
};

export const markTicketsAsSeen = (tickets: Array<Pick<TicketSummary, "id" | "updatedAt">>) => {
  const currentSeen = readLastSeenAt();
  const currentSeenTs = toTimestamp(currentSeen);
  const latestTicketTs = tickets.reduce((maxTs, ticket) => {
    const ticketTs = toTimestamp(ticket.updatedAt);
    if (Number.isNaN(ticketTs)) return maxTs;
    return Math.max(maxTs, ticketTs);
  }, Number.NEGATIVE_INFINITY);
  const nextSeenIso = latestTicketTs === Number.NEGATIVE_INFINITY
    ? new Date().toISOString()
    : new Date(latestTicketTs).toISOString();
  const nextSeenTs = toTimestamp(nextSeenIso);
  if (!Number.isNaN(currentSeenTs) && !Number.isNaN(nextSeenTs) && nextSeenTs <= currentSeenTs) {
    return;
  }
  writeLastSeenAt(nextSeenIso);
  notifyUnreadTicketsChanged();
};

export function useUnreadTickets(enabled: boolean) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const refreshUnreadTickets = useCallback(async () => {
    if (!enabled) {
      setUnreadCount(0);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await ticketService.getMyTickets({
        page: 0,
        size: 1,
      });
      const latest = response.items[0];
      if (!latest) {
        setUnreadCount(0);
        return;
      }
      const latestTs = toTimestamp(latest.updatedAt);
      const seenTs = toTimestamp(readLastSeenAt());
      if (Number.isNaN(latestTs) || Number.isNaN(seenTs)) {
        setUnreadCount(1);
        return;
      }
      setUnreadCount(latestTs > seenTs ? 1 : 0);
    } catch (error) {
      console.error("Failed to load unread ticket count", error);
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void refreshUnreadTickets();
  }, [refreshUnreadTickets]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleRefresh = () => {
      void refreshUnreadTickets();
    };
    window.addEventListener(UNREAD_TICKETS_EVENT, handleRefresh);
    return () => window.removeEventListener(UNREAD_TICKETS_EVENT, handleRefresh);
  }, [refreshUnreadTickets]);

  return {
    unreadCount,
    isLoading,
    refreshUnreadTickets,
  };
}
